// this (wdyr) MUST be loaded first in order for it to do it's magic
// import './wdyr';
import { StrictMode } from 'react';
import { type Root, createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { LoadingModal } from '@adc/parallax-component-library';

import { InitializationErrorPage, MainContainer, Providers } from 'Components/utility';

import { getApp, setUrls } from 'Utilities/dataHelpers';
import { _config, getEnvConfig } from 'Utilities/envConfig';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { createStore } from 'Utilities/store';

import App from './App';
import './index.css';
// Import the services via the index file in that folder
import './services';

// if (import.meta.env.DEV {
//   import('./dev');
//   App.whyDidYouRender = true;
// }

let store: ReturnType<typeof createStore>;
let root: Root;

// This is the starting point of our app launch life-cycle
const init = async () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  root = createRoot(document.getElementById('root')!);

  store = createStore();

  renderLoading();

  try {
    const config = await getEnvConfig();

    log.init(config.logLevel);

    if (config.mock) {
      mediator.once('MOCK:setup:intialized', () => initializeStore(config));

      // This import would allow you to have the mock code ready to go
      import('./services/mock').then(({ startMockWorker }) => {
        startMockWorker();
      });
    } else {
      initializeStore(config);
    }
  } catch (error) {
    log.error('Failed to get the environment config: ', error);
    renderError();
  }
};

const renderLoading = () => {
  root.render(
    <StrictMode>
      <Providers>
        <MainContainer>
          <LoadingModal aria-label="Loading" />
        </MainContainer>
      </Providers>
    </StrictMode>
  );
};

// Renders our UnexpectedError display and minimally bootstraps i18n
const renderError = () => {
  // since there was an error, need to initialize services anyway
  mediator.publish('init:i18n');

  root.render(
    <StrictMode>
      <InitializationErrorPage />
    </StrictMode>
  );
};

// Finally render our React trees
const renderApp = () => {
  try {
    root.render(
      <StrictMode>
        <Provider store={store}>
          <App />
        </Provider>
      </StrictMode>
    );
  } catch (e) {
    log.error(e);
    renderError();
  }
};

// Add code here that you would like to do when you have the env config ready
// This is likely going to be lagic to attempt getting an existing session
// eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
const initializeStore = (envConfig: typeof _config) => {
  setUrls(envConfig, getApp());

  store = createStore({ config: envConfig });

  mediator.publish('store:created');
};

// Begin
void init();

// Services that need to publish that they are ready
const appReady = {
  i18n: false,
  router: false,

  // state would be useful to track if redux store is ready
  // state: false,
};

// Listens for all services to be complete so that we have everything we need when we mount React
const readySub = mediator.subscribe('app:ready', (ready: Partial<typeof appReady>) => {
  Object.assign(appReady, ready);

  const done = Object.values(appReady).every((value) => !!value);

  // i18n sets the document title, so we tell our router to 'refresh' to get the
  // document title to update on Android
  if (ready.i18n) {
    mediator.publish('init:router');
  }

  if (done) {
    mediator.publish('app:mounted');
    mediator.remove('app:ready', readySub.id);
  }
});

// This block will only be called when the page is fully ready
// right now that means that our router service has spun up and done its initial work (redirecting if necessary)
// and the nav service has updated the store to reflect that.
// THEN 'app:ready' is published. otherwise the components may erroneously read the location information
// from the store (for example, the component may try reading it before the nav service has updated it, this
// was a problem fairly consistent with IE)
mediator.once('app:mounted', renderApp);
